import React, { useState, useEffect, useMemo } from "react";

import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import { bindActionCreators } from "redux";

import "./styles.css";

import { logout } from "../../../actions/baseActions";
import { setObj } from "../../../actions/noteActions";
import Breadcrumb from "../../../components/Breadcrumb";
import Loading from "../../../components/Loading";
import ProtectedRoute from "../../../components/ProtectedRoute";
import SelectFilter from "../../../components/SelectFilter";
import URL from "../../../config/api";
import axios from "../../../config/axios";

/* ============================= CONSTANTES ============================= */

const MONTH_NAMES = [
  "",
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez"
];

const ETAPA_LABEL = {
  fundamental: "Ensino Fundamental",
  infantil: "Educação Infantil"
};

const BAND_LABEL = ["Crítico", "Em desenvolvimento", "Consolidado"];
const BAND_CLASS = ["critico", "desenvolvimento", "consolidado"];
const BAND_COLOR = [
  "var(--status-critical)",
  "var(--status-warn)",
  "var(--status-good)"
];
const NEUTRAL_COLOR = "var(--text-muted)";

// Matemática usa sempre a mesma régua de 3 categorias em toda habilidade do
// Fundamental. Língua Portuguesa (Fundamental) e a Educação Infantil usam a
// régua real de cada ficha, que varia por item (de 2 a 6 categorias) — por
// isso cada item do catálogo já vem com sua própria lista de categorias
// (item.categories) na ordem certa, e um flag item.ordinal: quando false, as
// categorias são tipos/qualidades (não uma escala de "pior para melhor") e o
// item fica de fora do índice de desenvolvimento e do gráfico de 3 bandas.
function bandOf(index, total) {
  if (total === 2) return index === 0 ? 0 : 2; // binário (sim/não): sem "meio-termo"
  return Math.min(2, Math.floor((index * 3) / total));
}

function formatMesLabel(mesAno) {
  // mesAno vem como "YYYY-MM"
  const [year, month] = String(mesAno).split("-").map(Number);
  const label = `${MONTH_NAMES[month] || mesAno}/${String(year).slice(-2)}`;
  return label;
}

function fmt1(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return (Math.round(n * 10) / 10).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
}
function fmt0(n) {
  return Math.round(n || 0).toLocaleString("pt-BR");
}

function normalizeForSearch(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/* ============================= ÍCONES (KPIs) =============================
   Ícones de traço simples (estilo Feather/Lucide), embutidos como SVG inline
   — sem dependência externa — usados nos selos coloridos dos cartões de KPI. */
const ICON_PATHS = {
  users: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  bookCheck: (
    <>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5z" />
      <path d="m9 10 2 2 4-4" />
    </>
  ),
  alertTriangle: (
    <>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </>
  ),
  shieldCheck: (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  alertCircle: (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </>
  ),
  clipboardCheck: (
    <>
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <rect x="8" y="3" width="8" height="4" rx="1" />
      <path d="m9 14 2 2 4-4" />
    </>
  ),
  checkCircle: (
    <>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </>
  ),
  star: (
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  )
};

function IconSvg({ name }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {ICON_PATHS[name] || ICON_PATHS.star}
    </svg>
  );
}

/* ============================= HELPERS: ESCOPO E AGREGAÇÃO =============================
   Todas essas funções são puras: recebem o dataset já processado (`proc`) e o
   estado de navegação do painel (`panelState`) e devolvem dados derivados —
   nada de estado do React aqui, então recalcular a cada render é barato
   (o dataset já foi reduzido/indexado uma única vez no useMemo de `proc`). */

function itemsFor(proc, etapa, disc, serieId) {
  const byDisc = proc.catalog[etapa];
  if (!byDisc) return [];
  const bySerie = byDisc[disc];
  if (!bySerie) return [];
  return bySerie[String(serieId)] || [];
}

function allItemsFor(proc, etapa, disc, serieId) {
  const out = [];
  itemsFor(proc, etapa, disc, serieId).forEach(block =>
    block.items.forEach(item =>
      out.push({
        id: item.id,
        code: item.code,
        desc: item.desc,
        categories: item.categories,
        ordinal: item.ordinal !== false,
        unit: block.unit
      })
    )
  );
  return out;
}

function turmasInScope(proc, panelState) {
  let list = proc.turmas.filter(t => t.etapa === panelState.etapa);
  if (panelState.level !== "rede" && panelState.escolaId) {
    list = list.filter(t => t.escolaId === panelState.escolaId);
  }
  if (
    (panelState.level === "turma" || panelState.level === "aluno") &&
    panelState.turmaId
  ) {
    list = list.filter(t => t.id === panelState.turmaId);
  }
  if (panelState.serieId !== "todas") {
    list = list.filter(t => String(t.serieId) === String(panelState.serieId));
  }
  return list;
}

function alunosInScope(proc, panelState) {
  if (panelState.level === "aluno" && panelState.alunoId) {
    return proc.alunos.filter(a => a.id === panelState.alunoId);
  }
  const tIds = new Set(turmasInScope(proc, panelState).map(t => t.id));
  return proc.alunos.filter(a => tIds.has(a.turmaId));
}

// Estatísticas por item (habilidade), para um conjunto de alunos, em um mês,
// para a etapa+disciplina+série atuais. Uma entrada por unidade temática /
// campo de experiência, com o detalhamento por item aninhado dentro.
function computeItemStats(proc, alunosList, mesIdx, etapa, disc, serieId) {
  const blocks = itemsFor(proc, etapa, disc, serieId);
  return blocks.map(block => {
    const items = block.items.map(item => {
      const nCats = item.categories.length;
      const ordinal = item.ordinal !== false;
      const counts = new Array(nCats).fill(0);
      let avaliados = 0;
      const students = []; // {id, nome, v} — usado pelo "Ver alunos"
      alunosList.forEach(al => {
        if (String(al.serieId) !== String(serieId) || al.etapa !== etapa)
          return;
        const v = al.monthly[mesIdx][disc][item.id];
        if (v !== null && v !== undefined) {
          counts[v]++;
          avaliados++;
          students.push({ id: al.id, nome: al.nome, v });
        }
      });
      const bandCounts = [0, 0, 0];
      counts.forEach((c, i) => (bandCounts[bandOf(i, nCats)] += c));
      const bandPct = bandCounts.map(c =>
        avaliados ? (c / avaliados) * 100 : 0
      );
      return {
        id: item.id,
        code: item.code,
        desc: item.desc,
        categories: item.categories,
        ordinal,
        counts,
        avaliados,
        bandCounts,
        bandPct,
        students
      };
    });
    const bandCounts = [0, 0, 0];
    let avaliados = 0;
    items.forEach(it => {
      if (!it.ordinal) return; // itens não-evolutivos não entram na banda agregada da unidade
      it.bandCounts.forEach((c, i) => (bandCounts[i] += c));
      avaliados += it.avaliados;
    });
    const bandPct = bandCounts.map(c =>
      avaliados ? (c / avaliados) * 100 : 0
    );
    return { unit: block.unit, items, avaliados, bandCounts, bandPct };
  });
}

// Índice de desenvolvimento (0-100) para um conjunto de alunos em um mês,
// para a etapa+disciplina atuais. Médias entre todas as séries/itens
// relevantes no escopo. Itens não-evolutivos ficam de fora.
function computeIndice(proc, alunosList, mesIdx, etapa, disc) {
  let sum = 0;
  let n = 0;
  const bySerie = {};
  alunosList.forEach(al => {
    if (al.serieId == null || al.etapa !== etapa) return;
    (bySerie[al.serieId] = bySerie[al.serieId] || []).push(al);
  });
  Object.keys(bySerie).forEach(serieKey => {
    const items = allItemsFor(proc, etapa, disc, serieKey).filter(
      it => it.ordinal
    );
    bySerie[serieKey].forEach(al => {
      items.forEach(item => {
        const v = al.monthly[mesIdx][disc][item.id];
        if (v === null || v === undefined) return;
        sum += (v / (item.categories.length - 1)) * 100;
        n++;
      });
    });
  });
  return { indice: n ? sum / n : null, avaliacoes: n };
}

// Cobertura (participação) considera TODOS os itens, evolutivos ou não —
// mede se a ficha foi preenchida, não o nível de desenvolvimento.
function computeCobertura(proc, alunosList, mesIdx, etapa, disc) {
  let avaliados = 0;
  let possiveis = 0;
  alunosList.forEach(al => {
    if (al.serieId == null || al.etapa !== etapa) return;
    const items = allItemsFor(proc, etapa, disc, al.serieId);
    items.forEach(item => {
      possiveis++;
      const v = al.monthly[mesIdx][disc][item.id];
      if (v !== null && v !== undefined) avaliados++;
    });
  });
  return possiveis ? (avaliados / possiveis) * 100 : 0;
}

function computeTrendSeries(proc, alunosList, etapa, disc) {
  return proc.MONTHS.map((m, mi) => {
    const { indice } = computeIndice(proc, alunosList, mi, etapa, disc);
    const cobertura = computeCobertura(proc, alunosList, mi, etapa, disc);
    return { mes: m.label, indice, cobertura };
  });
}

/* ============================= COMPONENTES PEQUENOS ============================= */

// KPI tile com selo de ícone colorido — mesma linguagem visual do painel
// "Inteligência Educacional".
function KpiTile({ label, value, delta, deltaClass, accentClass, icon }) {
  return (
    <div className={`kpi-tile ${accentClass}`}>
      <div className="icon-badge">
        <IconSvg name={icon} />
      </div>
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {delta && <div className={`delta ${deltaClass || "flat"}`}>{delta}</div>}
    </div>
  );
}

// Selo "i" clicável com um balão de explicação. `openKey`/`onToggle` são
// compartilhados no nível do painel inteiro para que abrir um feche os
// outros — e para o balão sobreviver a re-renders sem perder o estado.
function InfoIcon({ myKey, ariaLabel, openKey, onToggle, children }) {
  const open = openKey === myKey;
  return (
    <span className="info-wrap">
      <button
        type="button"
        className={`info-btn${open ? " active" : ""}`}
        aria-label={ariaLabel}
        onClick={e => {
          e.stopPropagation();
          onToggle(open ? null : myKey);
        }}
      >
        i
      </button>
      <div
        className={`info-popover${open ? "" : " hidden"}`}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </span>
  );
}

// Controle segmentado (grupo de botões exclusivos) — usado para
// Etapa/Nível/Disciplina.
function SegmentedControl({ options, value, onChange }) {
  return (
    <div className="segmented">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          className={value === opt.value ? "active" : ""}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function bandChip(idx) {
  return (
    <span className={`chip ${BAND_CLASS[idx]}`}>
      <span className="dot" style={{ background: BAND_COLOR[idx] }} />
      {BAND_LABEL[idx]}
    </span>
  );
}

// Pill com o texto exato da categoria (ex.: "Desenvolveu com autonomia."),
// colorido pela banda correspondente — usado na lista de alunos do modal.
function CategoryChip({ label, colorVar }) {
  return (
    <span
      className="chip"
      style={{
        background: `color-mix(in srgb, ${colorVar} 16%, transparent)`,
        color: colorVar
      }}
    >
      <span className="dot" style={{ background: colorVar }} />
      {label}
    </span>
  );
}

/* ============================= FILTROS ============================= */

function scopeLabel(proc, panelState, redeLabel) {
  if (panelState.level === "rede") return `${redeLabel} (todas as escolas)`;
  const esc = proc.escolaById.get(panelState.escolaId);
  if (panelState.level === "escola") return esc ? esc.nome : "—";
  const t = proc.turmaById.get(panelState.turmaId);
  if (panelState.level === "turma")
    return t ? (esc ? `${esc.nome} — ` : "") + t.nome : "—";
  const al = proc.alunoById.get(panelState.alunoId);
  return al ? `${al.nome} · ${t ? t.nome : ""}` : "—";
}

function FiltersBar({
  proc,
  panelState,
  onChange,
  redeLabel,
  escolaOptions,
  serieOptions,
  turmaOptions,
  alunoOptions
}) {
  const lpLabel =
    panelState.etapa === "infantil" ? "Linguagem" : "Língua Portuguesa";

  return (
    <div className="filters">
      <div className="field">
        <label>Etapa</label>
        <SegmentedControl
          value={panelState.etapa}
          onChange={v =>
            onChange({
              etapa: v,
              serieId: "todas",
              turmaId: null,
              alunoId: null
            })
          }
          options={[
            { value: "fundamental", label: "Ensino Fundamental" },
            { value: "infantil", label: "Educação Infantil" }
          ]}
        />
      </div>

      <div className="field">
        <label>Nível</label>
        <SegmentedControl
          value={panelState.level}
          onChange={v => {
            const patch = { level: v };
            if (v === "rede") patch.escolaId = null;
            else if (!panelState.escolaId && proc.escolas.length)
              patch.escolaId = proc.escolas[0].id;
            onChange(patch);
          }}
          options={[
            { value: "rede", label: "Rede" },
            { value: "escola", label: "Escola" },
            { value: "turma", label: "Turma" },
            { value: "aluno", label: "Aluno" }
          ]}
        />
      </div>

      {panelState.level !== "rede" && (
        <div className="field">
          <label>Escola</label>
          <select
            value={panelState.escolaId || ""}
            onChange={e =>
              onChange({
                escolaId: e.target.value ? Number(e.target.value) : null,
                turmaId: null,
                alunoId: null
              })
            }
          >
            {escolaOptions.map(o => (
              <option key={o.id} value={o.id}>
                {o.nome}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="field">
        <label>Série</label>
        <select
          value={panelState.serieId}
          onChange={e =>
            onChange({ serieId: e.target.value, turmaId: null, alunoId: null })
          }
        >
          <option value="todas">Todas</option>
          {serieOptions.map(o => (
            <option key={o.id} value={o.id}>
              {o.nome}
            </option>
          ))}
        </select>
      </div>

      {(panelState.level === "turma" || panelState.level === "aluno") && (
        <div className="field">
          <label>Turma</label>
          <select
            value={panelState.turmaId || ""}
            onChange={e =>
              onChange({
                turmaId: e.target.value ? Number(e.target.value) : null,
                alunoId: null
              })
            }
          >
            {turmaOptions.map(t => {
              const esc = proc.escolaById.get(t.escolaId);
              return (
                <option key={t.id} value={t.id}>
                  {(esc ? `${esc.nome} — ` : "") + t.nome}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {panelState.level === "aluno" && (
        <div className="field">
          <label>Aluno</label>
          <select
            value={panelState.alunoId || ""}
            onChange={e =>
              onChange({
                alunoId: e.target.value ? Number(e.target.value) : null
              })
            }
          >
            {alunoOptions.map(a => (
              <option key={a.id} value={a.id}>
                {a.nome}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="field">
        <label>Disciplina</label>
        <SegmentedControl
          value={panelState.disc}
          onChange={v => onChange({ disc: v })}
          options={[
            { value: "lp", label: lpLabel },
            { value: "mat", label: "Matemática" }
          ]}
        />
      </div>

      {panelState.tab === "mes" && (
        <div className="field">
          <label>Mês (consolidado)</label>
          <select
            value={panelState.mesIdx}
            onChange={e => onChange({ mesIdx: Number(e.target.value) })}
          >
            {proc.MONTHS.map((m, i) => (
              <option key={m.k} value={i}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

/* ============================= PERFIL DOS ESTUDANTES ============================= */
// alfabetizado/PCD/laudo são atributos do cadastro do aluno, sem ligação com
// mês/disciplina — por isso este card não muda ao trocar disciplina ou mês.
function PerfilEstudantesCard({ proc, panelState, redeLabel }) {
  const scope = alunosInScope(proc, panelState);
  const total = scope.length;
  const pcdComLaudo = scope.filter(
    a => a.pcd === true && a.pcdComLaudo === true
  ).length;
  const pcdSemLaudo = scope.filter(
    a => a.pcd === true && a.pcdComLaudo !== true
  ).length;
  const pct = n => (total ? (n / total) * 100 : 0);
  const isInfantil = panelState.etapa === "infantil";

  const alfabetizadosCount = scope.filter(a => a.alfabetizado === true).length;
  const naoAlfabetizadosCount = scope.filter(
    a => a.alfabetizado === false
  ).length;

  return (
    <div className="card">
      <h2>Perfil dos estudantes</h2>
      <p className="card-sub">
        {scopeLabel(proc, panelState, redeLabel)} · dado do cadastro do aluno
        (professor), não muda por mês ou disciplina
        {isInfantil ? " · alfabetização não se aplica à Educação Infantil" : ""}
      </p>
      <div className={`kpi-row ${isInfantil ? "kpi-row-3" : "kpi-row-5"}`}>
        <KpiTile
          label="Total de estudantes"
          value={fmt0(total)}
          delta="no recorte selecionado"
          accentClass="k-teal"
          icon="users"
        />
        {!isInfantil && (
          <>
            <KpiTile
              label="Alfabetizados"
              value={fmt0(alfabetizadosCount)}
              delta={`${fmt1(pct(alfabetizadosCount))}% do total`}
              accentClass="k-green"
              icon="bookCheck"
            />
            <KpiTile
              label="Não alfabetizados"
              value={fmt0(naoAlfabetizadosCount)}
              delta={`${fmt1(pct(naoAlfabetizadosCount))}% do total`}
              accentClass="k-red"
              icon="alertTriangle"
            />
          </>
        )}
        <KpiTile
          label="Com deficiência (com laudo)"
          value={fmt0(pcdComLaudo)}
          delta={`${fmt1(pct(pcdComLaudo))}% do total`}
          accentClass="k-blue"
          icon="shieldCheck"
        />
        <KpiTile
          label="Com deficiência (sem laudo)"
          value={fmt0(pcdSemLaudo)}
          delta={`${fmt1(pct(pcdSemLaudo))}% do total`}
          accentClass="k-purple"
          icon="alertCircle"
        />
      </div>
    </div>
  );
}

/* ============================= ABA "CONSOLIDADO DO MÊS" — KPIs ============================= */

function KpiRow({ proc, panelState, openInfoKey, setOpenInfoKey }) {
  const scopeAlunos = alunosInScope(proc, panelState);
  const matric = scopeAlunos.length;
  const { indice } = computeIndice(
    proc,
    scopeAlunos,
    panelState.mesIdx,
    panelState.etapa,
    panelState.disc
  );
  const prevIdx =
    panelState.mesIdx > 0
      ? computeIndice(
          proc,
          scopeAlunos,
          panelState.mesIdx - 1,
          panelState.etapa,
          panelState.disc
        ).indice
      : null;
  const cobertura = computeCobertura(
    proc,
    scopeAlunos,
    panelState.mesIdx,
    panelState.etapa,
    panelState.disc
  );

  let avaliadosAlunos = 0;
  scopeAlunos.forEach(al => {
    if (al.serieId == null || al.etapa !== panelState.etapa) return;
    const items = allItemsFor(
      proc,
      panelState.etapa,
      panelState.disc,
      al.serieId
    );
    const any = items.some(
      it =>
        al.monthly[panelState.mesIdx][panelState.disc][it.id] !== null &&
        al.monthly[panelState.mesIdx][panelState.disc][it.id] !== undefined
    );
    if (any) avaliadosAlunos++;
  });

  let delta;
  let deltaClass = "flat";
  if (indice === null) {
    delta = "sem preenchimento neste mês";
  } else if (prevIdx !== null) {
    const d = indice - prevIdx;
    deltaClass = d > 0.4 ? "up" : d < -0.4 ? "down" : "flat";
    delta = `${(d > 0 ? "+" : "") + fmt1(d)} pts vs. mês anterior`;
  } else if (panelState.mesIdx > 0) {
    delta = "mês anterior sem preenchimento";
  } else {
    delta = "sem mês anterior para comparar";
  }

  const indiceValue =
    indice !== null ? (
      fmt1(indice)
    ) : (
      <span
        style={{ fontSize: 16, color: "var(--text-muted)", fontWeight: 600 }}
      >
        sem dados
      </span>
    );

  return (
    <div className="kpi-row">
      <KpiTile
        label="Estudantes matriculados"
        value={fmt0(matric)}
        delta="no recorte selecionado"
        accentClass="k-teal"
        icon="users"
      />
      <KpiTile
        label="Avaliados no mês (ao menos 1 item)"
        value={fmt0(avaliadosAlunos)}
        delta={`de ${fmt0(matric)} matriculados`}
        accentClass="k-blue"
        icon="clipboardCheck"
      />
      <KpiTile
        label="Cobertura da avaliação"
        value={`${fmt1(cobertura)}%`}
        delta="itens respondidos / itens possíveis"
        accentClass="k-green"
        icon="checkCircle"
      />
      <div className="kpi-tile k-amber">
        <div className="icon-badge">
          <IconSvg name="star" />
        </div>
        <div className="label">
          Índice de desenvolvimento médio
          <InfoIcon
            myKey="kpi-indice"
            ariaLabel="O que é o índice de desenvolvimento médio"
            openKey={openInfoKey}
            onToggle={setOpenInfoKey}
          >
            <b>Mostra, em uma nota de 0 a 100</b>, o quanto os alunos do recorte
            selecionado (rede, escola, turma ou aluno) já desenvolveram as
            habilidades avaliadas neste mês.
            <br />
            <br />
            Cada habilidade que o(a) professor(a) avaliou — por exemplo "não
            desenvolveu", "desenvolveu com ajuda" ou "desenvolveu com autonomia"
            — vira uma posição nessa régua de 0 a 100, e o índice é a média
            disso entre todas as habilidades e todos os alunos avaliados.
            <br />
            <br />
            <b>
              Quanto mais alto, mais alunos já dominam as habilidades com
              autonomia.
            </b>{" "}
            Não é uma prova com gabarito — é sobre nível de desenvolvimento, não
            acerto ou erro.
            <br />
            <br />
            Na <b>Educação Infantil</b>, boa parte das habilidades é avaliada
            como Sim/Não (sem meio-termo), então o índice ali funciona mais como
            "quantas habilidades a criança já alcançou" do que como uma escala
            fina de progresso.
          </InfoIcon>
        </div>
        <div className="value">{indiceValue}</div>
        <div className={`delta ${deltaClass}`}>{delta}</div>
      </div>
    </div>
  );
}

function discEmptyNoticeHtml(proc, panelState) {
  const total = proc.discTotalAnswers[panelState.etapa][panelState.disc];
  if (total > 0) return null;
  const discName =
    panelState.disc === "mat"
      ? "Matemática"
      : panelState.etapa === "infantil"
      ? "Linguagem"
      : "Língua Portuguesa";
  const etapaName = ETAPA_LABEL[panelState.etapa];
  const mesesLabel = proc.MONTHS.map(m => m.label).join(" e ");
  return (
    <div
      className="note-banner"
      style={{ borderLeftColor: "var(--status-warn)", marginBottom: 18 }}
    >
      <b>
        Ainda não há fichas de {discName} ({etapaName}) preenchidas em{" "}
        {mesesLabel}.
      </b>{" "}
      Os números abaixo aparecem zerados porque o(a) professor(a) ainda não
      preencheu essa combinação de etapa/disciplina nesses meses — não é um erro
      do painel.
    </div>
  );
}

/* ============================= DISTRIBUIÇÃO POR BANDA ============================= */

function stackedBarRow(label, bandPct, extraValue, key) {
  return (
    <div className="stackrow" key={key}>
      <div className="rowlabel">{label}</div>
      <div className="stackbar">
        {bandPct.map((pct, i) => (
          <div
            key={i}
            className="stackseg"
            style={{ width: `${pct}%`, background: BAND_COLOR[i] }}
            title={`${BAND_LABEL[i]}: ${fmt1(pct)}%`}
          />
        ))}
      </div>
      <div className="rowvalue">{extraValue}</div>
    </div>
  );
}

function DistribuicaoCard({ proc, panelState, serieOptions }) {
  const [showTable, setShowTable] = useState(false);
  const scopeAlunos = alunosInScope(proc, panelState);
  const serieList =
    panelState.serieId !== "todas"
      ? [panelState.serieId]
      : serieOptions.map(o => String(o.id));

  const combined = {};
  const order = [];
  serieList.forEach(serieId => {
    const list = scopeAlunos.filter(
      a => String(a.serieId) === String(serieId) && a.etapa === panelState.etapa
    );
    if (!list.length) return;
    const stats = computeItemStats(
      proc,
      list,
      panelState.mesIdx,
      panelState.etapa,
      panelState.disc,
      serieId
    );
    stats.forEach(s => {
      if (!combined[s.unit]) {
        combined[s.unit] = { bandCounts: [0, 0, 0], avaliados: 0 };
        order.push(s.unit);
      }
      combined[s.unit].bandCounts[0] += s.bandCounts[0];
      combined[s.unit].bandCounts[1] += s.bandCounts[1];
      combined[s.unit].bandCounts[2] += s.bandCounts[2];
      combined[s.unit].avaliados += s.avaliados;
    });
  });

  const discName =
    panelState.disc === "mat"
      ? "unidade temática (Matemática)"
      : panelState.etapa === "infantil"
      ? "campo de experiência (Linguagem)"
      : "eixo avaliado (Língua Portuguesa)";
  const totalAvaliados = order.reduce((s, u) => s + combined[u].avaliados, 0);

  let body;
  if (!order.length) {
    body = (
      <p style={{ color: "var(--text-muted)", fontSize: 12.5 }}>
        Sem avaliações registradas neste recorte.
      </p>
    );
  } else if (totalAvaliados === 0) {
    body = (
      <p style={{ color: "var(--text-muted)", fontSize: 12.5 }}>
        Nenhuma ficha preenchida em {proc.MONTHS[panelState.mesIdx].label} — sem
        preenchimento pelo(a) professor(a) neste mês (ex.: recesso escolar).
      </p>
    );
  } else {
    body = order.map(unit => {
      const c = combined[unit];
      const pct = c.avaliados
        ? c.bandCounts.map(v => (v / c.avaliados) * 100)
        : [0, 0, 0];
      return stackedBarRow(unit, pct, fmt0(c.avaliados), unit);
    });
  }

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h2>Distribuição por {discName}</h2>
          <p className="card-sub">
            {scopeLabel(proc, panelState, "")} ·{" "}
            {proc.MONTHS[panelState.mesIdx].label} · leitura rápida agrupada em
            3 níveis (veja o detalhamento por categoria abaixo)
          </p>
        </div>
        {!!order.length && (
          <button className="link-btn" onClick={() => setShowTable(s => !s)}>
            {showTable ? "Ver como gráfico" : "Ver como tabela"}
          </button>
        )}
      </div>
      <div className="legend">
        {BAND_LABEL.map((b, i) => (
          <span className="legend-item" key={b}>
            <span
              className="legend-dot"
              style={{ background: BAND_COLOR[i] }}
            />
            {b}
          </span>
        ))}
      </div>
      {!showTable && <div>{body}</div>}
      {showTable && !!order.length && (
        <div className="table-toggle-wrap">
          <table className="datatable">
            <thead>
              <tr>
                <th>
                  {panelState.disc === "mat"
                    ? "Unidade temática"
                    : panelState.etapa === "infantil"
                    ? "Campo de experiência"
                    : "Eixo"}
                </th>
                {BAND_LABEL.map(b => (
                  <th className="num" key={b}>
                    {b}
                  </th>
                ))}
                <th className="num">Avaliações</th>
              </tr>
            </thead>
            <tbody>
              {order.map(unit => {
                const c = combined[unit];
                const pct = c.avaliados
                  ? c.bandCounts.map(v => (v / c.avaliados) * 100)
                  : [0, 0, 0];
                return (
                  <tr key={unit}>
                    <td>{unit}</td>
                    {pct.map((p, i) => (
                      <td className="num" key={i}>
                        {fmt1(p)}%
                      </td>
                    ))}
                    <td className="num">{fmt0(c.avaliados)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============================= CONSOLIDADO POR CATEGORIA ============================= */

function CategoriaDetailCard({
  proc,
  panelState,
  serieOptions,
  onOpenStudentModal,
  openInfoKey,
  setOpenInfoKey
}) {
  const scopeAlunos = alunosInScope(proc, panelState);
  const serieList =
    panelState.serieId !== "todas"
      ? [panelState.serieId]
      : serieOptions.map(o => String(o.id));

  const units = [];
  const unitIndex = {};
  serieList.forEach(serieId => {
    const list = scopeAlunos.filter(
      a => String(a.serieId) === String(serieId) && a.etapa === panelState.etapa
    );
    if (!list.length) return;
    const stats = computeItemStats(
      proc,
      list,
      panelState.mesIdx,
      panelState.etapa,
      panelState.disc,
      serieId
    );
    stats.forEach(block => {
      if (!(block.unit in unitIndex)) {
        unitIndex[block.unit] = units.length;
        units.push({ unit: block.unit, items: [], itemIdx: {} });
      }
      const u = units[unitIndex[block.unit]];
      // Itens compartilhados entre séries (mesmo id) são somados numa única
      // linha ao ver "Todas" as séries, em vez de repetir um bloco por série.
      block.items.forEach(item => {
        if (item.id in u.itemIdx) {
          const existing = u.items[u.itemIdx[item.id]];
          existing.avaliados += item.avaliados;
          existing.counts = existing.counts.map((c, i) => c + item.counts[i]);
          existing.students = existing.students.concat(item.students);
        } else {
          u.itemIdx[item.id] = u.items.length;
          u.items.push({
            id: item.id,
            code: item.code,
            desc: item.desc,
            categories: item.categories,
            ordinal: item.ordinal !== false,
            counts: item.counts.slice(),
            avaliados: item.avaliados,
            students: item.students.slice()
          });
        }
      });
    });
  });

  const discName =
    panelState.disc === "mat"
      ? "Matemática"
      : panelState.etapa === "infantil"
      ? "Linguagem"
      : "Língua Portuguesa";

  return (
    <div className="card">
      <h2>
        Consolidado por categoria — {discName}
        <InfoIcon
          myKey="cat-detail"
          ariaLabel="Sobre o Consolidado por categoria"
          openKey={openInfoKey}
          onToggle={setOpenInfoKey}
        >
          <b>Consolidado por categoria:</b> além da visão geral por nível
          (crítico/em desenvolvimento/consolidado), cada habilidade/item da
          ficha aparece aqui com a{" "}
          <b>contagem exata em cada categoria original</b> (ex.: quantos alunos
          ainda não leem, quantos leem com fluência) — os mesmos números que
          hoje são lançados manualmente no <b>Criança Alfabetizada</b>, reunidos
          aqui por rede, escola ou turma para facilitar o transporte para o
          outro sistema.
        </InfoIcon>
      </h2>
      <p className="card-sub">
        {scopeLabel(proc, panelState, "")} ·{" "}
        {proc.MONTHS[panelState.mesIdx].label}
      </p>
      <p className="cat-detail-note">
        Quantidade e percentual de estudantes em cada categoria da ficha, por
        habilidade — prontos para lançar no Criança Alfabetizada.
      </p>
      <table className="datatable">
        <thead>
          <tr>
            <th>Categoria</th>
            <th></th>
            <th className="num">Quantidade</th>
            <th className="num">Percentual</th>
          </tr>
        </thead>
        <tbody>
          {!units.length && (
            <tr>
              <td
                colSpan={4}
                style={{ color: "var(--text-muted)", padding: "10px 8px" }}
              >
                Sem avaliações registradas neste recorte.
              </td>
            </tr>
          )}
          {units.map(u => {
            const skipSubHead =
              u.items.length === 1 &&
              !u.items[0].code &&
              u.items[0].desc.replace(/:\s*$/, "").trim() === u.unit.trim();
            return (
              <React.Fragment key={u.unit}>
                <tr className="item-head-row">
                  <td colSpan={4}>{u.unit}</td>
                </tr>
                {u.items.map(item => {
                  const nCats = item.categories.length;
                  const verAlunosBtn = item.avaliados > 0 && (
                    <>
                      {" "}
                      &middot;{" "}
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => onOpenStudentModal(item, u.unit)}
                      >
                        Ver alunos
                      </button>
                    </>
                  );
                  return (
                    <React.Fragment key={item.id}>
                      {!skipSubHead && (
                        <tr className="subitem-head-row">
                          <td colSpan={3}>
                            {item.code && <strong>{item.code}</strong>}{" "}
                            {item.code && "— "}
                            {item.desc}
                            {!item.ordinal && (
                              <span
                                style={{
                                  fontWeight: 400,
                                  color: "var(--text-muted)",
                                  fontSize: 11
                                }}
                              >
                                {" "}
                                (categorias descritivas — sem ordem de
                                desenvolvimento)
                              </span>
                            )}
                          </td>
                          <td className="num">
                            {fmt0(item.avaliados)} avaliações{verAlunosBtn}
                          </td>
                        </tr>
                      )}
                      {item.categories.map((catLabel, ci) => {
                        const qty = item.counts[ci];
                        const pct = item.avaliados
                          ? (qty / item.avaliados) * 100
                          : 0;
                        const color = item.ordinal
                          ? BAND_COLOR[bandOf(ci, nCats)]
                          : NEUTRAL_COLOR;
                        return (
                          <tr key={ci}>
                            <td
                              className="cat-label"
                              style={{ paddingLeft: 18 }}
                            >
                              {catLabel}
                            </td>
                            <td className="cat-bar-cell">
                              <div className="cat-bar-track">
                                <div
                                  className="cat-bar-fill"
                                  style={{
                                    width: `${pct}%`,
                                    background: color
                                  }}
                                />
                              </div>
                            </td>
                            <td className="num">{fmt0(qty)}</td>
                            <td className="num">{fmt1(pct)}%</td>
                          </tr>
                        );
                      })}
                      {skipSubHead && (
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: "0 8px 6px",
                              color: "var(--text-muted)",
                              fontSize: 11
                            }}
                          >
                            {fmt0(item.avaliados)} avaliações{verAlunosBtn}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ============================= COMPARATIVO (DRILLDOWN) ============================= */

function DrilldownCard({ proc, panelState }) {
  const mesIdx = panelState.mesIdx;
  let title;
  let cols;
  let rows = [];

  if (panelState.level === "rede") {
    title = "Comparativo entre escolas";
    cols = ["Escola", "Matriculados", "Cobertura", "Índice"];
    rows = proc.escolas
      .map(e => {
        const list = proc.alunos.filter(
          a =>
            a.escolaId === e.id &&
            a.etapa === panelState.etapa &&
            (panelState.serieId === "todas" ||
              String(a.serieId) === String(panelState.serieId))
        );
        const cobertura = computeCobertura(
          proc,
          list,
          mesIdx,
          panelState.etapa,
          panelState.disc
        );
        const { indice } = computeIndice(
          proc,
          list,
          mesIdx,
          panelState.etapa,
          panelState.disc
        );
        return { label: e.nome, matric: list.length, cobertura, indice };
      })
      .filter(r => r.matric > 0);
  } else if (panelState.level === "escola") {
    const esc = proc.escolaById.get(panelState.escolaId);
    title = `Comparativo entre turmas — ${esc ? esc.nome : ""}`;
    cols = ["Turma", "Matriculados", "Cobertura", "Índice"];
    rows = proc.turmas
      .filter(
        t =>
          t.escolaId === panelState.escolaId &&
          t.etapa === panelState.etapa &&
          (panelState.serieId === "todas" ||
            String(t.serieId) === String(panelState.serieId))
      )
      .map(t => {
        const list = proc.alunos.filter(a => a.turmaId === t.id);
        const cobertura = computeCobertura(
          proc,
          list,
          mesIdx,
          panelState.etapa,
          panelState.disc
        );
        const { indice } = computeIndice(
          proc,
          list,
          mesIdx,
          panelState.etapa,
          panelState.disc
        );
        return { label: t.nome, matric: list.length, cobertura, indice };
      });
  } else if (panelState.level === "turma") {
    const t = proc.turmaById.get(panelState.turmaId);
    title = `Alunos da turma — ${t ? t.nome : ""}`;
    cols = ["Aluno", "Cobertura", "Índice", "Nível geral"];
    rows = proc.alunos
      .filter(a => a.turmaId === panelState.turmaId)
      .map(a => {
        const cobertura = computeCobertura(
          proc,
          [a],
          mesIdx,
          panelState.etapa,
          panelState.disc
        );
        const { indice } = computeIndice(
          proc,
          [a],
          mesIdx,
          panelState.etapa,
          panelState.disc
        );
        const bandIdx =
          indice === null ? null : bandOf(Math.round((indice / 100) * 2), 3);
        return { label: a.nome, matric: null, cobertura, indice, bandIdx };
      });
  } else {
    const al = proc.alunoById.get(panelState.alunoId);
    title = `Detalhe por item — ${al ? al.nome : ""}`;
    const blocks = al
      ? itemsFor(proc, panelState.etapa, panelState.disc, al.serieId)
      : [];
    return (
      <div className="card">
        <h2>{title}</h2>
        <p className="card-sub">
          {proc.MONTHS[mesIdx].label} · classificação registrada pelo(a)
          professor(a) em cada habilidade
        </p>
        <table className="datatable">
          <thead>
            <tr>
              <th>
                {panelState.disc === "mat"
                  ? "Unidade temática"
                  : panelState.etapa === "infantil"
                  ? "Campo de experiência"
                  : "Eixo"}
              </th>
              <th>Habilidade</th>
              <th>Categorização</th>
              <th>Nível</th>
            </tr>
          </thead>
          <tbody>
            {!blocks.length && (
              <tr>
                <td
                  colSpan={4}
                  style={{ color: "var(--text-muted)", padding: "10px 8px" }}
                >
                  Sem itens cadastrados para esta série.
                </td>
              </tr>
            )}
            {blocks.map(block =>
              block.items.map(item => {
                const v = al.monthly[mesIdx][panelState.disc][item.id];
                const ordinal = item.ordinal !== false;
                const bandIdx =
                  !ordinal || v === null || v === undefined
                    ? null
                    : bandOf(v, item.categories.length);
                const catLabel =
                  v === null || v === undefined
                    ? "Não avaliado neste mês"
                    : item.categories[v];
                return (
                  <tr key={item.id}>
                    <td>{block.unit}</td>
                    <td>
                      {item.code && <strong>{item.code}</strong>}{" "}
                      {item.code && "— "}
                      {item.desc}
                    </td>
                    <td>{catLabel}</td>
                    <td>
                      {!ordinal ? (
                        <span style={{ color: "var(--text-muted)" }}>
                          — (descritivo)
                        </span>
                      ) : bandIdx === null ? (
                        <span style={{ color: "var(--text-muted)" }}>—</span>
                      ) : (
                        bandChip(bandIdx)
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    );
  }

  rows.sort((a, b) => (b.indice || 0) - (a.indice || 0));

  return (
    <div className="card">
      <h2>{title}</h2>
      <p className="card-sub">
        {proc.MONTHS[mesIdx].label} · ordenado por índice de desenvolvimento
      </p>
      <table className="datatable">
        <thead>
          <tr>
            {cols.map(c => (
              <th
                key={c}
                className={
                  c === "Escola" || c === "Turma" || c === "Aluno" ? "" : "num"
                }
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{r.label}</td>
              {r.matric !== null && r.matric !== undefined && (
                <td className="num">{fmt0(r.matric)}</td>
              )}
              <td className="num">{fmt1(r.cobertura)}%</td>
              <td className="num">
                {r.indice !== null ? fmt1(r.indice) : "—"}
              </td>
              {panelState.level === "turma" && (
                <td>
                  {r.bandIdx === null ? (
                    <span style={{ color: "var(--text-muted)" }}>sem dado</span>
                  ) : (
                    bandChip(r.bandIdx)
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ============================= GRÁFICO DE LINHA (SVG) ============================= */

function LineChartSVG({ seriesMain, seriesContext, contextLabel }) {
  const [hover, setHover] = useState(null); // { i, x, y }
  const w = 640;
  const h = 220;
  const padL = 34;
  const padR = 46;
  const padT = 16;
  const padB = 28;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const n = seriesMain.length;
  const x = i => padL + (n > 1 ? (innerW * i) / (n - 1) : innerW / 2);
  const y = v => padT + innerH - innerH * (v / 100);

  const pathFor = (series, key) => {
    let d = "";
    let started = false;
    series.forEach((pt, i) => {
      const v = pt[key];
      if (v === null || v === undefined) {
        started = false;
        return;
      }
      d += `${(started ? "L" : "M") + x(i)},${y(v)} `;
      started = true;
    });
    return d;
  };

  const gridLines = [0, 25, 50, 75, 100].map(v => (
    <React.Fragment key={v}>
      <line
        x1={padL}
        x2={w - padR}
        y1={y(v)}
        y2={y(v)}
        stroke="var(--gridline)"
        strokeWidth="1"
      />
      <text
        x={padL - 8}
        y={y(v) + 3}
        fontSize="10"
        textAnchor="end"
        fill="var(--text-muted)"
      >
        {v}
      </text>
    </React.Fragment>
  ));

  const xLabels = seriesMain.map((pt, i) => {
    if (i % 2 !== 0 && i !== n - 1) return null;
    return (
      <text
        key={i}
        x={x(i)}
        y={h - 8}
        fontSize="10"
        textAnchor="middle"
        fill="var(--text-muted)"
      >
        {pt.mes}
      </text>
    );
  });

  const gapMarkers = seriesMain.map((pt, i) => {
    if (pt.indice !== null && pt.indice !== undefined) return null;
    return (
      <React.Fragment key={i}>
        <line
          x1={x(i)}
          x2={x(i)}
          y1={padT}
          y2={padT + innerH}
          stroke="var(--text-muted)"
          strokeWidth="1"
          strokeDasharray="2 3"
          opacity="0.6"
        />
        <text
          x={x(i)}
          y={padT + 12}
          fontSize="9.5"
          textAnchor="middle"
          fill="var(--text-muted)"
        >
          sem dados
        </text>
      </React.Fragment>
    );
  });

  const dots = seriesMain.map((pt, i) => {
    if (pt.indice === null || pt.indice === undefined) return null;
    return (
      <circle
        key={i}
        cx={x(i)}
        cy={y(pt.indice)}
        r="4"
        fill="var(--series-1)"
        stroke="var(--surface-1)"
        strokeWidth="2"
      />
    );
  });

  const lastPt = [...seriesMain]
    .reverse()
    .find(p => p.indice !== null && p.indice !== undefined);
  const lastLabel = lastPt && (
    <text
      x={x(seriesMain.indexOf(lastPt)) + 8}
      y={y(lastPt.indice) + 4}
      fontSize="11"
      fontWeight="600"
      fill="var(--text-primary)"
    >
      {fmt1(lastPt.indice)}
    </text>
  );

  const activePt = hover !== null ? seriesMain[hover] : null;
  const activeCtx =
    hover !== null && seriesContext ? seriesContext[hover] : null;

  return (
    <div className="chart-svg-wrap">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        width="100%"
        height={h}
        className="linechart"
      >
        {gridLines}
        {xLabels}
        {gapMarkers}
        {seriesContext && (
          <path
            d={pathFor(seriesContext, "indice")}
            fill="none"
            stroke="var(--text-muted)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="4 3"
          />
        )}
        <path
          d={pathFor(seriesMain, "indice")}
          fill="none"
          stroke="var(--series-1)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {dots}
        {lastLabel}
        {seriesMain.map((pt, i) => (
          <rect
            key={i}
            x={x(i) - innerW / (2 * Math.max(1, n - 1)) - 2}
            y={padT}
            width={innerW / Math.max(1, n - 1)}
            height={innerH}
            fill="transparent"
            onMouseMove={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          />
        ))}
      </svg>
      {activePt && (
        <div
          className="tooltip visible"
          style={{ left: x(hover), top: y(activePt.indice ?? 50) }}
        >
          <strong>{activePt.mes}</strong>
          <br />
          {activePt.indice === null || activePt.indice === undefined ? (
            "Sem preenchimento neste mês"
          ) : (
            <>
              Índice: <strong>{fmt1(activePt.indice)}</strong>
              <br />
              Cobertura: <strong>{fmt1(activePt.cobertura)}%</strong>
              {seriesContext && activeCtx && (
                <>
                  <br />
                  <span style={{ opacity: 0.75 }}>
                    {contextLabel}:{" "}
                    {activeCtx.indice !== null ? fmt1(activeCtx.indice) : "—"}
                  </span>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ============================= ABA "PROGRESSO MENSAL" ============================= */

function contextSeriesFor(proc, panelState, redeLabel) {
  if (panelState.level === "escola") {
    const list = proc.alunos.filter(
      a =>
        a.etapa === panelState.etapa &&
        (panelState.serieId === "todas" ||
          String(a.serieId) === String(panelState.serieId))
    );
    return {
      label: `${redeLabel} (rede)`,
      series: computeTrendSeries(proc, list, panelState.etapa, panelState.disc)
    };
  }
  if (panelState.level === "turma") {
    const list = proc.alunos.filter(
      a => a.escolaId === panelState.escolaId && a.etapa === panelState.etapa
    );
    return {
      label: "Média da escola",
      series: computeTrendSeries(proc, list, panelState.etapa, panelState.disc)
    };
  }
  if (panelState.level === "aluno") {
    const list = proc.alunos.filter(a => a.turmaId === panelState.turmaId);
    return {
      label: "Média da turma",
      series: computeTrendSeries(proc, list, panelState.etapa, panelState.disc)
    };
  }
  return null;
}

function ProgressoTrendCard({ proc, panelState, redeLabel }) {
  const scopeAlunos = alunosInScope(proc, panelState);
  const seriesMain = computeTrendSeries(
    proc,
    scopeAlunos,
    panelState.etapa,
    panelState.disc
  );
  const ctx = contextSeriesFor(proc, panelState, redeLabel);
  const label = scopeLabel(proc, panelState, redeLabel);
  const discLabel =
    panelState.disc === "mat"
      ? "Matemática"
      : panelState.etapa === "infantil"
      ? "Linguagem"
      : "Língua Portuguesa";

  return (
    <div className="card">
      <h2>Progresso do índice de desenvolvimento</h2>
      <p className="card-sub">
        {label} · {discLabel} · {proc.MONTHS[0]?.label} a{" "}
        {proc.MONTHS[proc.MONTHS.length - 1]?.label}
      </p>
      <div className="legend">
        <span className="legend-item">
          <span
            className="legend-dot"
            style={{ background: "var(--series-1)" }}
          />
          {label}
        </span>
        {ctx && (
          <span className="legend-item">
            <span
              className="legend-dot"
              style={{ background: "var(--text-muted)" }}
            />
            {ctx.label} (referência)
          </span>
        )}
      </div>
      <LineChartSVG
        seriesMain={seriesMain}
        seriesContext={ctx ? ctx.series : null}
        contextLabel="Referência"
      />
    </div>
  );
}

function CoberturaCard({ proc, panelState }) {
  const scopeAlunos = alunosInScope(proc, panelState);
  const seriesMain = computeTrendSeries(
    proc,
    scopeAlunos,
    panelState.etapa,
    panelState.disc
  );
  const remapped = seriesMain.map(p => ({
    mes: p.mes,
    indice: p.cobertura,
    cobertura: p.cobertura
  }));
  return (
    <div className="card">
      <h2>Cobertura da avaliação ao longo do ano</h2>
      <p className="card-sub">
        % de itens avaliados sobre o total possível, por mês
      </p>
      <LineChartSVG seriesMain={remapped} seriesContext={null} />
    </div>
  );
}

function RankingCard({ proc, panelState }) {
  let title;
  let rows = [];
  const lastIdx = proc.MONTHS.length - 1;

  if (panelState.level === "rede") {
    title = "Variação do índice no ano — por escola";
    rows = proc.escolas
      .map(e => {
        const list = proc.alunos.filter(
          a =>
            a.escolaId === e.id &&
            a.etapa === panelState.etapa &&
            (panelState.serieId === "todas" ||
              String(a.serieId) === String(panelState.serieId))
        );
        const first = computeIndice(
          proc,
          list,
          0,
          panelState.etapa,
          panelState.disc
        ).indice;
        const last = computeIndice(
          proc,
          list,
          lastIdx,
          panelState.etapa,
          panelState.disc
        ).indice;
        return {
          label: e.nome,
          delta: last !== null && first !== null ? last - first : null,
          matric: list.length
        };
      })
      .filter(r => r.matric > 0);
  } else if (panelState.level === "escola") {
    title = "Variação do índice no ano — por turma";
    rows = proc.turmas
      .filter(
        t =>
          t.escolaId === panelState.escolaId &&
          t.etapa === panelState.etapa &&
          (panelState.serieId === "todas" ||
            String(t.serieId) === String(panelState.serieId))
      )
      .map(t => {
        const list = proc.alunos.filter(a => a.turmaId === t.id);
        const first = computeIndice(
          proc,
          list,
          0,
          panelState.etapa,
          panelState.disc
        ).indice;
        const last = computeIndice(
          proc,
          list,
          lastIdx,
          panelState.etapa,
          panelState.disc
        ).indice;
        return {
          label: t.nome,
          delta: last !== null && first !== null ? last - first : null
        };
      });
  } else if (panelState.level === "turma") {
    title = "Variação do índice no ano — por aluno";
    rows = proc.alunos
      .filter(a => a.turmaId === panelState.turmaId)
      .map(a => {
        const first = computeIndice(
          proc,
          [a],
          0,
          panelState.etapa,
          panelState.disc
        ).indice;
        const last = computeIndice(
          proc,
          [a],
          lastIdx,
          panelState.etapa,
          panelState.disc
        ).indice;
        return {
          label: a.nome,
          delta: last !== null && first !== null ? last - first : null
        };
      });
  } else {
    title = "Evolução por habilidade";
    const al = proc.alunoById.get(panelState.alunoId);
    const items = al
      ? allItemsFor(proc, panelState.etapa, panelState.disc, al.serieId).filter(
          it => it.ordinal
        )
      : [];
    rows = items.map(item => {
      const v0 = al.monthly[0][panelState.disc][item.id];
      const v1 = al.monthly[lastIdx][panelState.disc][item.id];
      const first =
        v0 !== null && v0 !== undefined
          ? (v0 / (item.categories.length - 1)) * 100
          : null;
      const last =
        v1 !== null && v1 !== undefined
          ? (v1 / (item.categories.length - 1)) * 100
          : null;
      const label = item.code
        ? item.code
        : item.desc.length > 44
        ? `${item.desc.slice(0, 44)}…`
        : item.desc;
      return {
        label,
        delta: first !== null && last !== null ? last - first : null
      };
    });
  }

  rows.sort((a, b) => (b.delta || -999) - (a.delta || -999));
  const maxAbs = Math.max(1, ...rows.map(r => Math.abs(r.delta || 0)));

  return (
    <div className="card">
      <h2>{title}</h2>
      <p className="card-sub">
        Diferença do índice de desenvolvimento entre {proc.MONTHS[0]?.label} e{" "}
        {proc.MONTHS[lastIdx]?.label} (pontos)
      </p>
      {rows.map((r, i) => {
        const d = r.delta;
        const pct = d === null ? 0 : (Math.abs(d) / maxAbs) * 100;
        const color =
          d === null
            ? "var(--text-muted)"
            : d >= 0
            ? "var(--status-good)"
            : "var(--status-critical)";
        const dirLabel =
          d === null ? "sem dado" : (d >= 0 ? "+" : "") + fmt1(d);
        return (
          <div className="stackrow" key={i}>
            <div className="rowlabel">{r.label}</div>
            <div className="stackbar" style={{ background: "var(--gridline)" }}>
              <div
                className="stackseg"
                style={{ width: `${pct}%`, background: color }}
              />
            </div>
            <div className="rowvalue">{dirLabel}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ============================= MODAL "VER ALUNOS" ============================= */
// Listas de estudantes podem passar de 100-1000 nomes — em vez de expandir
// inline na tabela, abre um painel sobreposto com altura fixa, scroll
// próprio, busca por nome e ordenação por nome ou por status.

function StudentModal({ item, unitLabel, onClose }) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ field: "nome", dir: 1 });
  const searchInputRef = React.useRef(null);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const students = useMemo(() => {
    const nCats = item.categories.length;
    return (item.students || []).map(s => {
      const label = item.categories[s.v];
      const color = item.ordinal
        ? BAND_COLOR[bandOf(s.v, nCats)]
        : NEUTRAL_COLOR;
      return {
        nome: s.nome,
        nomeNorm: normalizeForSearch(s.nome),
        catIdx: s.v,
        label,
        color
      };
    });
  }, [item]);

  const q = normalizeForSearch(search);
  const filtered = (
    q ? students.filter(s => s.nomeNorm.includes(q)) : students.slice()
  ).sort((a, b) => {
    let cmp =
      sort.field === "status"
        ? a.catIdx - b.catIdx
        : a.nome.localeCompare(b.nome, "pt-BR");
    if (cmp === 0) cmp = a.nome.localeCompare(b.nome, "pt-BR");
    return cmp * sort.dir;
  });

  const toggleSort = field => {
    setSort(s =>
      s.field === field ? { field, dir: s.dir * -1 } : { field, dir: 1 }
    );
  };
  const sortArrow = field =>
    sort.field === field ? (sort.dir === 1 ? " ▲" : " ▼") : "";

  const titleLabel =
    (item.code ? `${item.code} — ` : "") + item.desc.replace(/:\s*$/, "");

  return (
    <div
      className="modal-backdrop"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-panel" role="dialog" aria-modal="true">
        <div className="modal-head">
          <div>
            <h3>{titleLabel}</h3>
            <p className="modal-sub">
              {`${
                (unitLabel ? `${unitLabel} · ` : "") + fmt0(item.avaliados)
              } avaliações neste recorte`}
            </p>
          </div>
          <button
            type="button"
            className="modal-close"
            aria-label="Fechar"
            onClick={onClose}
          >
            &times;
          </button>
        </div>
        <div className="modal-search">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Buscar aluno pelo nome..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <p className="modal-count">
          {filtered.length} de {students.length} aluno(s)
          {q ? ` · filtrado por "${search}"` : ""}
        </p>
        <div className="modal-body">
          {!filtered.length ? (
            <div
              style={{
                padding: "10px 4px",
                color: "var(--text-muted)",
                fontSize: 12.5
              }}
            >
              Nenhum aluno encontrado.
            </div>
          ) : (
            <table className="datatable">
              <thead>
                <tr>
                  <th
                    onClick={() => toggleSort("nome")}
                    title="Ordenar por nome"
                  >
                    Nome{sortArrow("nome")}
                  </th>
                  <th
                    onClick={() => toggleSort("status")}
                    title="Ordenar por status"
                  >
                    Status{sortArrow("status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={i}>
                    <td>{s.nome}</td>
                    <td>
                      <CategoryChip label={s.label} colorVar={s.color} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================= COMPONENTE PRINCIPAL ============================= */

const ConsolidatedPedagogicalFollowUps = ({ user, general, currentRoute }) => {
  // --- ESCOPO DE PERMISSÃO (grupo/regional/escola) — igual cascata de antes ---
  const [lists, setLists] = useState({ groups: [], regions: [], schools: [] });
  const [scopeFilters, setScopeFilters] = useState(() => ({
    groupID: general.active_roles.group_id || null,
    regionID: general.active_roles.region_id || null,
    schoolID: general.active_roles.school_id || null
  }));

  // --- DADOS DO PAINEL (dataset bruto + navegação client-side) ---
  const [panelLoading, setPanelLoading] = useState(false);
  const [panelData, setPanelData] = useState(null);
  const [panelState, setPanelState] = useState({
    level: "rede",
    escolaId: null,
    turmaId: null,
    alunoId: null,
    etapa: "fundamental",
    serieId: "todas",
    disc: "lp",
    mesIdx: 0,
    tab: "mes"
  });
  const [studentModalItem, setStudentModalItem] = useState(null);
  const [openInfoKey, setOpenInfoKey] = useState(null);

  const config = useMemo(
    () => ({ headers: { Authorization: `Bearer ${user.token}` } }),
    [user.token]
  );

  // 1. Carga inicial de grupos
  useEffect(() => {
    axios
      .get(`${URL.api}access/group/list`, config)
      .then(res =>
        setLists(p => ({
          ...p,
          groups:
            res.data?.data?.map(g => ({ label: g.name, value: g.id })) || []
        }))
      )
      .catch(() => toastr.error("Erro", "Falha ao carregar grupos."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Regionais
  useEffect(() => {
    if (!scopeFilters.groupID) return;
    axios
      .get(
        `${URL.api}access/region/listRegionFilter/${scopeFilters.groupID}`,
        config
      )
      .then(res =>
        setLists(p => ({
          ...p,
          regions:
            res.data?.data?.map(r => ({ label: r.name, value: r.id })) || []
        }))
      );
  }, [scopeFilters.groupID, config]);

  // 3. Escolas
  useEffect(() => {
    if (!scopeFilters.groupID || !scopeFilters.regionID) return;
    axios
      .get(
        `${URL.api}access/school/listSchoolFilter/${scopeFilters.groupID}/${scopeFilters.regionID}`,
        config
      )
      .then(res =>
        setLists(p => ({
          ...p,
          schools:
            res.data?.data?.map(s => ({ label: s.name, value: s.id })) || []
        }))
      );
  }, [scopeFilters.groupID, scopeFilters.regionID, config]);

  const redeLabel = useMemo(() => {
    const g = lists.groups.find(g => g.value === scopeFilters.groupID);
    return g ? g.label : "Rede selecionada";
  }, [lists.groups, scopeFilters.groupID]);

  // --- CARREGAR O PAINEL (chamada única — depois disso tudo é client-side) ---
  const handleLoadPanel = async () => {
    if (!scopeFilters.groupID) {
      return toastr.warning(
        "Atenção",
        "Selecione o Grupo antes de carregar o painel."
      );
    }
    setPanelLoading(true);
    try {
      // eslint-disable-next-line camelcase -- nomes exigidos pela query string da API (Validator do backend espera snake_case)
      const params = { group_id: scopeFilters.groupID };
      /* eslint-disable camelcase -- idem acima */
      if (scopeFilters.regionID) params.region_id = scopeFilters.regionID;
      if (scopeFilters.schoolID) params.school_id = scopeFilters.schoolID;
      /* eslint-enable camelcase */

      const res = await axios.get(`${URL.api}pedagogical/follow-ups/painel`, {
        params,
        ...config
      });
      if (!res.data?.success) {
        toastr.error(
          "Erro",
          res.data?.message || "Falha ao carregar o painel."
        );
        return;
      }
      const data = res.data.data;
      setPanelData(data);
      const { series_meses: seriesMesesLoaded } = data;
      const meses = seriesMesesLoaded || [];
      setPanelState(p => ({
        ...p,
        mesIdx: Math.max(0, meses.length - 1),
        level: "rede",
        escolaId: null,
        turmaId: null,
        alunoId: null,
        serieId: "todas"
      }));
      if (!meses.length) {
        toastr.info("Aviso", "Nenhum dado encontrado para esse escopo.");
      }
    } catch {
      toastr.error("Erro", "Falha ao carregar os dados do painel.");
    } finally {
      setPanelLoading(false);
    }
  };

  const handleClearAll = () => {
    setScopeFilters({
      groupID: general.active_roles.group_id || null,
      regionID: general.active_roles.region_id || null,
      schoolID: general.active_roles.school_id || null
    });
    setPanelData(null);
    setStudentModalItem(null);
  };

  // --- PROCESSAMENTO DO DATASET BRUTO (uma vez por carga do painel) ---
  const proc = useMemo(() => {
    if (!panelData) return null;
    const {
      series_meses: seriesMeses,
      series,
      catalog,
      escolas,
      turmas,
      alunos,
      answers
    } = panelData;

    const MONTHS = (seriesMeses || []).map(m => ({
      k: m,
      label: formatMesLabel(m)
    }));
    const mesIndexOf = new Map(MONTHS.map((m, i) => [m.k, i]));

    const turmaById = new Map((turmas || []).map(t => [t.id, t]));
    const escolaById = new Map((escolas || []).map(e => [e.id, e]));

    const itemMeta = new Map();
    ["fundamental", "infantil"].forEach(etapa => {
      const byDisc = (catalog && catalog[etapa]) || {};
      Object.keys(byDisc).forEach(disc => {
        const bySerie = byDisc[disc] || {};
        Object.keys(bySerie).forEach(serieKey => {
          (bySerie[serieKey] || []).forEach(block =>
            block.items.forEach(item =>
              itemMeta.set(item.id, { disc, etapa, serieId: serieKey })
            )
          );
        });
      });
    });

    const alunosProc = (alunos || []).map(a => {
      const t = turmaById.get(a.turmaId) || {};
      return {
        ...a,
        serieId: t.serieId != null ? String(t.serieId) : null,
        etapa: t.etapa || null,
        escolaId: t.escolaId != null ? t.escolaId : null,
        monthly: MONTHS.map(() => ({ lp: {}, mat: {} }))
      };
    });
    const alunoById = new Map(alunosProc.map(a => [a.id, a]));

    const discTotalAnswers = {
      fundamental: { lp: 0, mat: 0 },
      infantil: { lp: 0, mat: 0 }
    };

    (answers || []).forEach(row => {
      const [mesAno, , alunoId, itemId, tier] = row;
      const al = alunoById.get(alunoId);
      const meta = itemMeta.get(itemId);
      const mi = mesIndexOf.get(mesAno);
      if (!al || !meta || mi === undefined) return;
      al.monthly[mi][meta.disc][itemId] = tier;
      discTotalAnswers[meta.etapa][meta.disc]++;
    });

    return {
      MONTHS,
      mesIndexOf,
      turmaById,
      escolaById,
      alunos: alunosProc,
      alunoById,
      turmas: turmas || [],
      escolas: escolas || [],
      catalog: catalog || { fundamental: {}, infantil: {} },
      series: series || { fundamental: [], infantil: [] },
      discTotalAnswers
    };
  }, [panelData]);

  // --- LISTAS DERIVADAS PARA OS FILTROS ---
  const serieOptions = useMemo(
    () => (proc ? proc.series[panelState.etapa] || [] : []),
    [proc, panelState.etapa]
  );

  const turmaOptions = useMemo(() => {
    if (!proc) return [];
    let list = proc.turmas.filter(t => t.etapa === panelState.etapa);
    if (panelState.escolaId)
      list = list.filter(t => t.escolaId === panelState.escolaId);
    if (panelState.serieId !== "todas")
      list = list.filter(t => String(t.serieId) === String(panelState.serieId));
    return list;
  }, [proc, panelState.etapa, panelState.escolaId, panelState.serieId]);

  const alunoOptions = useMemo(() => {
    if (!proc || !panelState.turmaId) return [];
    return proc.alunos.filter(a => a.turmaId === panelState.turmaId);
  }, [proc, panelState.turmaId]);

  // Auto-corrige turma/aluno selecionados quando saem da lista válida (ex.:
  // trocou a etapa ou a série e a turma anterior não existe mais no recorte).
  useEffect(() => {
    if (!proc || (panelState.level !== "turma" && panelState.level !== "aluno"))
      return;
    if (!turmaOptions.find(t => t.id === panelState.turmaId)) {
      setPanelState(p => ({
        ...p,
        turmaId: turmaOptions.length ? turmaOptions[0].id : null,
        alunoId: null
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    proc,
    panelState.level,
    panelState.etapa,
    panelState.escolaId,
    panelState.serieId,
    turmaOptions
  ]);

  useEffect(() => {
    if (!proc || panelState.level !== "aluno") return;
    if (!alunoOptions.find(a => a.id === panelState.alunoId)) {
      setPanelState(p => ({
        ...p,
        alunoId: alunoOptions.length ? alunoOptions[0].id : null
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proc, panelState.level, panelState.turmaId, alunoOptions]);

  return (
    <div className="container-fluid">
      <Breadcrumb
        items={[
          { name: "Acompanhamento Pedagógico", to: "/pedagogical-follow-ups/" }
        ]}
      />

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Escopo dos dados</h5>
          <button
            className="btn btn-sm btn-outline-light"
            onClick={handleClearAll}
          >
            Limpar seleção
          </button>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-4 mb-3">
              <SelectFilter
                title="Grupo *"
                value={scopeFilters.groupID}
                options={lists.groups}
                disabled={!!general?.active_roles?.group_id}
                setFunction={id =>
                  setScopeFilters(p => ({
                    ...p,
                    groupID: id,
                    regionID: null,
                    schoolID: null
                  }))
                }
              />
            </div>
            <div className="col-md-4 mb-3">
              <SelectFilter
                title="Regional"
                value={scopeFilters.regionID}
                options={lists.regions}
                disabled={
                  !scopeFilters.groupID || !!general?.active_roles?.region_id
                }
                setFunction={id =>
                  setScopeFilters(p => ({ ...p, regionID: id, schoolID: null }))
                }
              />
            </div>
            <div className="col-md-4 mb-3">
              <SelectFilter
                title="Escola"
                value={scopeFilters.schoolID}
                options={lists.schools}
                disabled={
                  !scopeFilters.regionID || !!general?.active_roles?.school_id
                }
                setFunction={id =>
                  setScopeFilters(p => ({ ...p, schoolID: id }))
                }
              />
            </div>
            <div className="col-md-12 text-right">
              <button
                className="btn btn-primary"
                onClick={handleLoadPanel}
                disabled={panelLoading || !scopeFilters.groupID}
              >
                {panelLoading ? (
                  <i className="fa fa-spinner fa-spin mr-2" />
                ) : (
                  <i className="fa fa-search mr-2" />
                )}
                Carregar Painel
              </button>
            </div>
          </div>
        </div>
      </div>

      {panelLoading && <Loading />}

      {!panelLoading && !proc && (
        <div className="note-banner">
          Selecione o grupo (e, se quiser, a regional/escola) e clique em
          "Carregar Painel" para começar.
        </div>
      )}

      {!panelLoading && proc && (
        <div className="pedagogical-panel-root">
          <div className="wrap">
            <header className="top">
              <div>
                <h1>
                  Acompanhamento Pedagógico — {redeLabel}
                  <InfoIcon
                    myKey="header"
                    ariaLabel="Sobre estes dados"
                    openKey={openInfoKey}
                    onToggle={setOpenInfoKey}
                  >
                    <b>Dados da rede selecionada.</b> Os números vêm diretamente
                    do banco de dados (fichas de acompanhamento pedagógico
                    preenchidas pelo(a) professor(a), por turma). A apuração é{" "}
                    <b>mensal</b>: os números de escola e rede são somas das
                    turmas, não um lançamento à parte. Um mês sem nenhum
                    preenchimento aparece como "sem dados", não como zero.
                  </InfoIcon>
                </h1>
                <p className="sub">
                  Consolidado das fichas de acompanhamento pedagógico do Ensino
                  Fundamental (1º ao 5º ano) e da Educação Infantil, preenchidas
                  pelo(a) professor(a) por turma, com visão da rede, da escola,
                  da turma ou do aluno.
                </p>
              </div>
            </header>

            <FiltersBar
              proc={proc}
              panelState={panelState}
              onChange={patch => setPanelState(p => ({ ...p, ...patch }))}
              redeLabel={redeLabel}
              escolaOptions={proc.escolas}
              serieOptions={serieOptions}
              turmaOptions={turmaOptions}
              alunoOptions={alunoOptions}
            />

            <PerfilEstudantesCard
              proc={proc}
              panelState={panelState}
              redeLabel={redeLabel}
            />

            <div className="tabs">
              <button
                className={panelState.tab === "mes" ? "active" : ""}
                onClick={() => setPanelState(p => ({ ...p, tab: "mes" }))}
              >
                Consolidado do mês
              </button>
              <button
                className={panelState.tab === "progresso" ? "active" : ""}
                onClick={() => setPanelState(p => ({ ...p, tab: "progresso" }))}
              >
                Progresso mensal
              </button>
            </div>

            {discEmptyNoticeHtml(proc, panelState)}

            {panelState.tab === "mes" ? (
              <>
                <div className="section-caption">
                  Consolidado do mês
                  <InfoIcon
                    myKey="mes-caption"
                    ariaLabel="Sobre o índice de desenvolvimento e a cobertura"
                    openKey={openInfoKey}
                    onToggle={setOpenInfoKey}
                  >
                    <b>Sobre a métrica de progresso:</b> cada habilidade
                    avaliada (Matemática) ou item do eixo avaliado (Língua
                    Portuguesa/Linguagem) é classificado pelo(a) professor(a) em
                    uma de três categorias — "não desenvolveu", "desenvolveu com
                    ajuda do(a) professor(a)" ou "desenvolveu com autonomia".
                    Por isso o painel usa um{" "}
                    <b>índice de desenvolvimento (0–100)</b> calculado a partir
                    dessa régua, além da <b>cobertura</b> (% de itens
                    respondidos sobre o total possível no mês) — combinando
                    participação e nível de desenvolvimento, sem depender de
                    gabarito.
                  </InfoIcon>
                </div>
                <KpiRow
                  proc={proc}
                  panelState={panelState}
                  openInfoKey={openInfoKey}
                  setOpenInfoKey={setOpenInfoKey}
                />
                <DistribuicaoCard
                  proc={proc}
                  panelState={panelState}
                  serieOptions={serieOptions}
                />
                {panelState.level !== "aluno" && (
                  <CategoriaDetailCard
                    proc={proc}
                    panelState={panelState}
                    serieOptions={serieOptions}
                    onOpenStudentModal={(item, unitLabel) =>
                      setStudentModalItem({ item, unitLabel })
                    }
                    openInfoKey={openInfoKey}
                    setOpenInfoKey={setOpenInfoKey}
                  />
                )}
                <DrilldownCard proc={proc} panelState={panelState} />
              </>
            ) : (
              <>
                <div className="grid-2">
                  <ProgressoTrendCard
                    proc={proc}
                    panelState={panelState}
                    redeLabel={redeLabel}
                  />
                  <RankingCard proc={proc} panelState={panelState} />
                </div>
                <CoberturaCard proc={proc} panelState={panelState} />
              </>
            )}

            <div className="foot-note">
              Painel de Acompanhamento Pedagógico · dados do escopo selecionado
            </div>
          </div>

          {studentModalItem && (
            <StudentModal
              item={studentModalItem.item}
              unitLabel={studentModalItem.unitLabel}
              onClose={() => setStudentModalItem(null)}
            />
          )}
        </div>
      )}

      <ProtectedRoute routeId={currentRoute} />
    </div>
  );
};

const mapStateToProps = state => ({ user: state.user, general: state.general });
const mapDispatchToProps = dispatch =>
  bindActionCreators({ logout, setObj }, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ConsolidatedPedagogicalFollowUps);
